import { Injectable } from '@angular/core';
import { BiometricAuth, CheckBiometryResult, BiometryType } from '@aparajita/capacitor-biometric-auth';
import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { TranslateService } from '@ngx-translate/core';

const BIOMETRIC_ENABLED_KEY = 'biometricEnabled';
const BIOMETRIC_CREDENTIALS_KEY = 'biometricCredentials';
const LOGIN_COUNT_KEY = 'loginCount';
const BIOMETRIC_PROMPT_DISMISSED_KEY = 'biometricPromptDismissed';
const BIOMETRIC_REMIND_LATER_KEY = 'biometricRemindLater';

export interface BiometricCredentials {
  email: string;
  password: string;
}

@Injectable({
  providedIn: 'root'
})
export class BiometricService {
  constructor(private translateService: TranslateService) {}

  /**
   * Check if biometric authentication is available on the device
   */
  async isAvailable(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      console.log('[BiometricService] Not on native platform');
      return false;
    }

    try {
      const result: CheckBiometryResult = await BiometricAuth.checkBiometry();
      console.log('[BiometricService] Biometry check result:', result);
      return result.isAvailable;
    } catch (error) {
      console.error('[BiometricService] Error checking biometry availability:', error);
      return false;
    }
  }

  /**
   * Get the type of biometric authentication available
   */
  async getBiometryType(): Promise<BiometryType> {
    try {
      const result: CheckBiometryResult = await BiometricAuth.checkBiometry();
      return result.biometryType;
    } catch (error) {
      console.error('[BiometricService] Error getting biometry type:', error);
      return BiometryType.none;
    }
  }

  /**
   * Get a user-friendly name for the biometry type
   */
  async getBiometryName(): Promise<string> {
    const type = await this.getBiometryType();
    switch (type) {
      case BiometryType.fingerprintAuthentication:
        return this.translateService.instant('BIOMETRIC_PROMPT.TYPE_FINGERPRINT');
      case BiometryType.faceAuthentication:
        return this.translateService.instant('BIOMETRIC_PROMPT.TYPE_FACE_ID');
      case BiometryType.irisAuthentication:
        return this.translateService.instant('BIOMETRIC_PROMPT.TYPE_IRIS');
      default:
        return this.translateService.instant('BIOMETRIC_PROMPT.TYPE_BIOMETRIC');
    }
  }

  /**
   * Check if biometric authentication is enabled by user preference
   */
  async isBiometricEnabled(): Promise<boolean> {
    try {
      const { value } = await Preferences.get({ key: BIOMETRIC_ENABLED_KEY });
      return value === 'true';
    } catch (error) {
      console.error('[BiometricService] Error checking biometric preference:', error);
      return false;
    }
  }

  /**
   * Set biometric authentication preference
   */
  async setBiometricEnabled(enabled: boolean): Promise<void> {
    try {
      await Preferences.set({ key: BIOMETRIC_ENABLED_KEY, value: enabled ? 'true' : 'false' });
      console.log('[BiometricService] Biometric enabled set to:', enabled);
    } catch (error) {
      console.error('[BiometricService] Error setting biometric preference:', error);
    }
  }

  /**
   * Authenticate using biometrics
   * @param reason - The reason to show to the user
   */
  async authenticate(reason?: string): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      console.warn('[BiometricService] Not on native platform');
      return false;
    }

    const subtitle = reason ?? this.translateService.instant('BIOMETRIC_PROMPT.REASON_AUTHENTICATE');

    try {
      await BiometricAuth.authenticate({
        reason: '', // Empty reason to avoid left-aligned text
        cancelTitle: this.translateService.instant('COMMON.CANCEL'),
        allowDeviceCredential: false,
        iosFallbackTitle: this.translateService.instant('BIOMETRIC_PROMPT.USE_PASSCODE'),
        androidTitle: this.translateService.instant('BIOMETRIC_PROMPT.ANDROID_TITLE'),
        androidSubtitle: subtitle, // Use subtitle for centered text
        androidConfirmationRequired: false
      });
      console.log('[BiometricService] Authentication successful');
      return true;
    } catch (error) {
      console.error('[BiometricService] Authentication failed:', error);
      return false;
    }
  }

  /**
   * Store user credentials securely for biometric login
   * Note: Credentials are stored in Preferences. For production,
   * consider using a more secure storage solution.
   */
  async saveCredentials(email: string, password: string): Promise<void> {
    try {
      const credentials: BiometricCredentials = { email, password };
      await Preferences.set({
        key: BIOMETRIC_CREDENTIALS_KEY,
        value: JSON.stringify(credentials)
      });
      console.log('[BiometricService] Credentials saved');
    } catch (error) {
      console.error('[BiometricService] Error saving credentials:', error);
      throw error;
    }
  }

  /**
   * Retrieve stored credentials
   */
  async getCredentials(): Promise<BiometricCredentials | null> {
    try {
      const { value } = await Preferences.get({ key: BIOMETRIC_CREDENTIALS_KEY });
      if (value) {
        return JSON.parse(value) as BiometricCredentials;
      }
      return null;
    } catch (error) {
      console.error('[BiometricService] Error getting credentials:', error);
      return null;
    }
  }

  /**
   * Clear stored credentials only (keeps biometric enabled state)
   * Use this for logout - user preference to use biometric is preserved
   */
  async clearStoredCredentials(): Promise<void> {
    try {
      await Preferences.remove({ key: BIOMETRIC_CREDENTIALS_KEY });
      console.log('[BiometricService] Stored credentials cleared (biometric still enabled)');
    } catch (error) {
      console.error('[BiometricService] Error clearing stored credentials:', error);
    }
  }

  /**
   * Clear stored credentials AND disable biometric
   * Use this for password resets - credentials are invalid so disable feature
   */
  async clearCredentials(): Promise<void> {
    try {
      await Preferences.remove({ key: BIOMETRIC_CREDENTIALS_KEY });
      await this.setBiometricEnabled(false);
      console.log('[BiometricService] Credentials cleared and biometric disabled');
    } catch (error) {
      console.error('[BiometricService] Error clearing credentials:', error);
    }
  }

  /**
   * Check if credentials are stored
   */
  async hasStoredCredentials(): Promise<boolean> {
    const credentials = await this.getCredentials();
    return credentials !== null;
  }

  /**
   * Complete biometric setup - authenticate and save credentials
   * Note: With the new flow, credentials are saved at login time,
   * so this method primarily just verifies biometric works and enables it
   */
  async setupBiometric(email: string, password: string): Promise<boolean> {
    try {
      // First check if biometric is available
      const available = await this.isAvailable();
      if (!available) {
        console.warn('[BiometricService] Biometric not available');
        return false;
      }

      // Request authentication to verify the user can use biometrics
      const biometryName = await this.getBiometryName();
      const authenticated = await this.authenticate(
        this.translateService.instant('BIOMETRIC_PROMPT.ENABLE_QUICK_LOGIN', { type: biometryName })
      );

      if (authenticated) {
        // Save credentials and enable biometric
        await this.saveCredentials(email, password);
        await this.setBiometricEnabled(true);
        console.log('[BiometricService] Biometric setup complete');
        return true;
      }

      return false;
    } catch (error) {
      console.error('[BiometricService] Error during biometric setup:', error);
      return false;
    }
  }

  /**
   * Enable biometric authentication (modern UX - no password required)
   * Assumes credentials were already saved during login
   * Just verifies biometric works and enables the feature
   */
  async enableBiometric(): Promise<boolean> {
    try {
      // First check if biometric is available
      const available = await this.isAvailable();
      if (!available) {
        console.warn('[BiometricService] Biometric not available');
        return false;
      }

      // Check if credentials are already stored
      const hasCredentials = await this.hasStoredCredentials();
      if (!hasCredentials) {
        console.warn('[BiometricService] No credentials stored - user needs to log in first');
        return false;
      }

      // Request authentication to verify the user can use biometrics
      const biometryName = await this.getBiometryName();
      const authenticated = await this.authenticate(
        this.translateService.instant('BIOMETRIC_PROMPT.VERIFY_ENABLE', { type: biometryName })
      );

      if (authenticated) {
        // Just enable biometric - credentials already stored
        await this.setBiometricEnabled(true);
        console.log('[BiometricService] Biometric enabled successfully');
        return true;
      }

      return false;
    } catch (error) {
      console.error('[BiometricService] Error enabling biometric:', error);
      return false;
    }
  }

  /**
   * Increment login counter
   */
  async incrementLoginCount(): Promise<number> {
    try {
      const count = await this.getLoginCount();
      const newCount = count + 1;
      await Preferences.set({ key: LOGIN_COUNT_KEY, value: newCount.toString() });
      console.log('[BiometricService] Login count incremented to:', newCount);
      return newCount;
    } catch (error) {
      console.error('[BiometricService] Error incrementing login count:', error);
      return 1;
    }
  }

  /**
   * Get current login count
   */
  async getLoginCount(): Promise<number> {
    try {
      const { value } = await Preferences.get({ key: LOGIN_COUNT_KEY });
      return value ? parseInt(value, 10) : 0;
    } catch (error) {
      console.error('[BiometricService] Error getting login count:', error);
      return 0;
    }
  }

  /**
   * Check if we should show the biometric setup prompt
   * Show on 2nd login if:
   * - Biometric is available
   * - Not already enabled
   * - User hasn't permanently dismissed
   * - User hasn't recently said "Remind Me Later"
   */
  async shouldShowSetupPrompt(): Promise<boolean> {
    try {
      const available = await this.isAvailable();
      if (!available) {
        console.log('[BiometricService] Biometric not available');
        return false;
      }

      const enabled = await this.isBiometricEnabled();
      if (enabled) {
        console.log('[BiometricService] Biometric already enabled');
        return false;
      }

      const loginCount = await this.getLoginCount();
      if (loginCount < 2) {
        console.log('[BiometricService] Not enough logins yet:', loginCount);
        return false;
      }

      const dismissed = await this.isPromptPermanentlyDismissed();
      if (dismissed) {
        console.log('[BiometricService] User permanently dismissed prompt');
        return false;
      }

      const remindLater = await this.isRemindLaterActive();
      if (remindLater) {
        console.log('[BiometricService] User selected Remind Me Later recently');
        return false;
      }

      return true;
    } catch (error) {
      console.error('[BiometricService] Error checking if should show prompt:', error);
      return false;
    }
  }

  /**
   * Mark prompt as permanently dismissed (user clicked "No Thanks")
   */
  async dismissPromptPermanently(): Promise<void> {
    try {
      await Preferences.set({ key: BIOMETRIC_PROMPT_DISMISSED_KEY, value: 'true' });
      console.log('[BiometricService] Prompt permanently dismissed');
    } catch (error) {
      console.error('[BiometricService] Error dismissing prompt:', error);
    }
  }

  /**
   * Check if prompt was permanently dismissed
   */
  async isPromptPermanentlyDismissed(): Promise<boolean> {
    try {
      const { value } = await Preferences.get({ key: BIOMETRIC_PROMPT_DISMISSED_KEY });
      return value === 'true';
    } catch (error) {
      console.error('[BiometricService] Error checking dismissed status:', error);
      return false;
    }
  }

  /**
   * Set remind later timestamp (show again after 3 logins)
   */
  async setRemindLater(): Promise<void> {
    try {
      const currentCount = await this.getLoginCount();
      const remindAtCount = currentCount + 3; // Ask again after 3 more logins
      await Preferences.set({ key: BIOMETRIC_REMIND_LATER_KEY, value: remindAtCount.toString() });
      console.log('[BiometricService] Remind later set for login count:', remindAtCount);
    } catch (error) {
      console.error('[BiometricService] Error setting remind later:', error);
    }
  }

  /**
   * Check if "Remind Me Later" is still active
   */
  async isRemindLaterActive(): Promise<boolean> {
    try {
      const { value } = await Preferences.get({ key: BIOMETRIC_REMIND_LATER_KEY });
      if (!value) return false;

      const remindAtCount = parseInt(value, 10);
      const currentCount = await this.getLoginCount();

      return currentCount < remindAtCount;
    } catch (error) {
      console.error('[BiometricService] Error checking remind later status:', error);
      return false;
    }
  }

  /**
   * Reset all biometric prompting preferences (for testing)
   */
  async resetPromptPreferences(): Promise<void> {
    try {
      await Preferences.remove({ key: BIOMETRIC_PROMPT_DISMISSED_KEY });
      await Preferences.remove({ key: BIOMETRIC_REMIND_LATER_KEY });
      await Preferences.remove({ key: LOGIN_COUNT_KEY });
      console.log('[BiometricService] Prompt preferences reset');
    } catch (error) {
      console.error('[BiometricService] Error resetting preferences:', error);
    }
  }
}
